import { useState } from "react";
import { PackageCheck, RefreshCw, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Chip, Field, NSelect, Panel, useFetch } from "@/components/common";
import { ConfirmDialog } from "@/components/DemoCleanup";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fDate } from "@/lib/api";

const CLS = { envoye: "bg-sky-50 text-sky-700 border-sky-200", echec: "bg-rose-50 text-rose-700 border-rose-200", commande: "bg-amber-50 text-amber-700 border-amber-200", recu: "bg-emerald-50 text-emerald-700 border-emerald-200" };

export function OrderButton({ data, reload }) {
  const [open, setOpen] = useState(false);
  const p = data.pending;
  const send = async () => {
    try {
      const { data: o } = await api.post("/adblue-orders");
      toast[o.status === "envoye" ? "success" : "error"](o.status === "envoye" ? "Demande de commande envoyée à la Direction" : "Demande enregistrée, mais l'e-mail n'a pas pu partir");
      setOpen(false); reload();
    } catch (e) { toast.error(errMsg(e)); setOpen(false); reload(); }
  };
  return (
    <div className="mb-6" data-testid="adblue-order-block">
      {p && <div className="rounded-xl bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 mb-3 text-sm font-semibold" data-testid="adblue-order-pending">Commande AdBlue en attente · {p.quantity} bidons · demandée le {p.date_fr} à {p.time} par {p.requested_by_name} · <Chip cls={CLS[p.status]}>{p.status_label}</Chip></div>}
      <Button disabled={!!p} onClick={() => setOpen(true)} className="w-full sm:w-auto h-12 px-6 bg-[#F49619] hover:bg-[#E0830B] text-[#0E2D43] font-bold text-base disabled:bg-slate-300 disabled:text-slate-600" data-testid="adblue-order-btn">
        <ShoppingCart className="w-5 h-5 mr-2" /> Commander de l'AdBlue
      </Button>
      {open && (
        <ConfirmDialog title="Commander de l'AdBlue" label="Envoyer la demande" primary onConfirm={send} onClose={() => setOpen(false)} testid="adblue-order-confirm">
          <p>Le stock d'AdBlue est arrivé au seuil de réserve ({data.settings.threshold} bidons restants). Envoyer une demande de commande de <b>{data.settings.quantity} bidons</b> ?</p>
          <p className="text-xs text-slate-500">Un e-mail sera envoyé à {data.settings.recipient}.</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

function OrderRow({ o, manage, reload }) {
  const act = async (fn, ok) => { try { await fn(); toast.success(ok); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <tr className="border-b last:border-0" data-testid={`adblue-order-row-${o.id}`}>
      <td className="py-2 pr-3 whitespace-nowrap">{o.date_fr} {o.time}</td>
      <td className="pr-3">{o.requested_by_name}</td>
      <td className="pr-3">{o.quantity} bidons</td>
      <td className="pr-3">{o.threshold} restants</td>
      <td className="pr-3">{o.recipient}</td>
      <td className="pr-3"><Chip cls={CLS[o.status]} testid={`adblue-order-status-${o.id}`}>{o.status_label}</Chip>{o.received_at && <span className="text-xs text-slate-500 ml-1">le {fDate(o.received_at)}</span>}</td>
      {manage && (
        <td className="py-2 flex flex-wrap gap-2 items-center">
          {o.status !== "recu" && <div className="w-40"><NSelect value={o.status === "echec" ? "" : o.status} placeholder="Statut…" onChange={(s) => act(() => api.patch(`/adblue-orders/${o.id}`, { status: s }), "Statut mis à jour")} testid={`adblue-order-set-${o.id}`} options={[{ value: "envoye", label: "Demande envoyée" }, { value: "commande", label: "Commandé" }, { value: "recu", label: "Réceptionné" }]} /></div>}
          {o.status === "echec" && <Button size="sm" variant="outline" onClick={async () => { try { const { data: r } = await api.post(`/adblue-orders/${o.id}/resend`); toast[r.result.status === "envoye" ? "success" : "error"](r.result.status === "envoye" ? "E-mail renvoyé" : "Nouvel échec d'envoi de l'e-mail"); reload(); } catch (e) { toast.error(errMsg(e)); } }} data-testid={`adblue-order-resend-${o.id}`}><RefreshCw className="w-3.5 h-3.5 mr-1" />Renvoyer l'e-mail</Button>}
        </td>
      )}
    </tr>
  );
}

function OrderSettings({ s, reload }) {
  const [f, setF] = useState({ threshold: s.threshold, quantity: s.quantity, recipient: s.recipient });
  const save = async () => {
    try { await api.put("/settings/adblue-order", { threshold: Number(f.threshold), quantity: Number(f.quantity), recipient: f.recipient }); toast.success("Paramètres enregistrés"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid sm:grid-cols-4 gap-3 items-end mt-4 pt-4 border-t" data-testid="adblue-order-settings">
      <Field label="Seuil (bidons restants)"><Input type="number" value={f.threshold} onChange={(e) => setF({ ...f, threshold: e.target.value })} data-testid="adblue-set-threshold" /></Field>
      <Field label="Quantité à commander"><Input type="number" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} data-testid="adblue-set-quantity" /></Field>
      <Field label="Destinataire e-mail"><Input value={f.recipient} onChange={(e) => setF({ ...f, recipient: e.target.value })} data-testid="adblue-set-recipient" /></Field>
      <Button onClick={save} className="bg-[#0E2D43]" data-testid="adblue-set-save">Enregistrer</Button>
    </div>
  );
}

export function AdBlueOrders() {
  const { user } = useAuth();
  const { data, reload } = useFetch("/adblue-orders");
  if (!data) return null;
  const manage = user.role === "admin";
  const trigger = user.role === "chauffeur" || (user.role === "admin" && !user.real_role);
  return (
    <>
      {trigger ? <OrderButton data={data} reload={reload} /> : data.pending && <div className="rounded-xl bg-amber-50 border border-amber-300 text-amber-800 px-4 py-3 mb-6 text-sm font-semibold" data-testid="adblue-order-pending">Commande AdBlue en attente · {data.pending.quantity} bidons · {data.pending.status_label}</div>}
      <Panel title="Demandes de commande AdBlue" testid="adblue-orders-panel" className="mb-6">
        {!data.orders.length ? <p className="text-sm text-slate-500">Aucune demande pour l'instant.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm"><thead><tr className="text-left text-xs text-slate-500 border-b"><th className="py-2">Date / heure</th><th>Demandé par</th><th>Quantité</th><th>Seuil</th><th>Destinataire</th><th>Statut</th>{manage && <th />}</tr></thead>
            <tbody>{data.orders.map((o) => <OrderRow key={o.id} o={o} manage={manage} reload={reload} />)}</tbody></table></div>
        )}
        {manage && <OrderSettings s={data.settings} reload={reload} />}
        {!manage && <p className="text-xs text-slate-500 mt-3 flex items-center gap-1"><PackageCheck className="w-3.5 h-3.5" />Le bouton se réactive dès que la Direction marque la commande « Réceptionné ».</p>}
      </Panel>
    </>
  );
}
